import { type ReactEventHandler, useCallback, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { observer } from 'mobx-react';

import {
  GAME_LAUNCH_CATEGORY,
  GAME_LAUNCH_HORIZONTAL_LOCATION,
  GAME_LAUNCH_VERTICAL_LOCATION,
  GAME_LAUNCH_VIEW,
} from '../../../config/constants';
import { usePhoneVerifyDialog } from '../../../hooks/usePhoneVerifyDialog';
import { useStartGame } from '../../../hooks/useStartGame';
import { useVisibility } from '../../../hooks/useVisibility';
import { analytics } from '../../../services/analytics';
import { useFlag } from '../../../services/launchDarkly';
import type { Game } from '../../../stores/game/types';
import { useStores } from '../../../stores/useStores';
import { getThumbnailUrl } from '../../../utils/getThumbnailUrl';
import { JackpotBalance } from '../../JackpotBalance/JackpotBalance';
import { PreloadBlur } from '../../PreloadBlur/PreloadBlur';
import { GAME_CARD_ID_PREFIX } from '../constants';
import { CrownJackpotBadge } from '../CrownJackpotBadge/CrownJackpotBadge';
import { jackpotCoinsAttr } from '../CrownJackpotBadge/jackpotCoinsAttr';
import { getGamePosition } from '../getGamePosition';
import { useGameLabels } from '../hooks/useGameLabels';
import { isJackpotGame } from '../isJackpotGame';
import { isRepeatTap } from '../lobbyLaunchTracking';
import { PlayingNowSlot } from '../PlayingNowBadge/PlayingNowSlot';
import { getLobbyView, sendTapImpression } from '../seenGamesImpression';
import { TestingBadge } from '../TestingBadge/TestingBadge';

import styles from './GameCardV2.module.scss';

export const GameCardV2 = observer(
  ({
    game,
    rowName,
    gameLabelFF,
    onboarding,
    tooltipRef,
    carousel,
    animated = true,
    duplicate,
  }: {
    game: Game;
    rowName: string;
    gameLabelFF: boolean;
    onboarding?: boolean;
    tooltipRef?: null | ((el: HTMLElement | null) => void);
    carousel?: boolean;
    animated?: boolean;
    /** See `GameCardProps` in GameCard.tsx — a marquee copy must not claim the card's `id`. */
    duplicate?: boolean;
  }) => {
    const { userStore, coinTypeStore } = useStores();
    const { tagGameLabels, iconGameLabels } = useGameLabels(game, gameLabelFF);

    const { masterCasinoGameId, identifier, name: gameName } = game;

    const { coinType } = coinTypeStore;

    const isJackpot = isJackpotGame(game);

    const [shouldStickVertically, setShouldStickVertically] = useState(false);

    const { ref, visible } = useVisibility<HTMLAnchorElement>({
      defaultState: false,
      disable: !isJackpot,
    });

    const [isImageLoaded, setIsImageLoaded] = useState(false);

    const { handleStartGame } = useStartGame();
    const { openVerifyPhoneDialog } = usePhoneVerifyDialog();

    const isWaitingForVerification = useRef(false);

    const isPhoneVerificationRequired = useFlag('phone_verification_dialog', true);
    const phoneVerified =
      !isPhoneVerificationRequired || (userStore.profile?.phone && userStore.profile?.phoneVerified);

    const cardRef = useRef<HTMLAnchorElement | null>(null);

    const startGame = useCallback(() => {
      if (isRepeatTap(masterCasinoGameId)) {
        return;
      }
      if (cardRef.current) {
        sendTapImpression(cardRef.current, rowName);
      }
      const position = cardRef.current ? getGamePosition(cardRef.current) : null;
      if (position) {
        localStorage.setItem(GAME_LAUNCH_VERTICAL_LOCATION, String(position.vertical_location));
        localStorage.setItem(GAME_LAUNCH_HORIZONTAL_LOCATION, String(position.horizontal_location));
      }
      localStorage.setItem(GAME_LAUNCH_CATEGORY, rowName);
      const lobbyView = getLobbyView();
      localStorage.setItem(GAME_LAUNCH_VIEW, lobbyView);

      void analytics.sendEvent('game_launch', {
        event_category: 'lobby_page',
        event_type: 'game_id',
        event_value: `${game.masterCasinoGameId}`,
        event_action: 'click',
        category: rowName,
        lobby_view: lobbyView,
        ...(position && {
          vertical_location: position.vertical_location,
          horizontal_location: position.horizontal_location,
        }),
      });
      handleStartGame(masterCasinoGameId, rowName);
    }, [handleStartGame, masterCasinoGameId, rowName, game.masterCasinoGameId]);

    const attemptStartingGame = useCallback(
      (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();

        if (!phoneVerified && coinType === 'SC') {
          let success = false;
          isWaitingForVerification.current = true;

          const cancelVerification = () => {
            isWaitingForVerification.current = success;
          };

          const onSuccess = () => {
            success = true;
          };

          openVerifyPhoneDialog(onSuccess, {
            beforeClose: cancelVerification,
          });
        } else {
          startGame();
        }
      },
      [openVerifyPhoneDialog, startGame, phoneVerified, coinType],
    );

    useEffect(() => {
      if (isWaitingForVerification.current && phoneVerified) {
        // Once: startGame changes identity after the launch navigates, which would launch again.
        isWaitingForVerification.current = false;
        startGame();
      }
    }, [phoneVerified, startGame]);

    const handleImageLoad = useCallback<ReactEventHandler<HTMLImageElement>>((event) => {
      setIsImageLoaded(true);
      const { height, width, parentElement } = event.currentTarget;

      if (!parentElement) {
        return;
      }

      const parentRatio = parentElement.clientHeight / parentElement.clientWidth;
      const ownRatio = height / width;

      setShouldStickVertically(parentRatio > ownRatio);
    }, []);

    const mergedRef = useCallback(
      (element: HTMLAnchorElement | null) => {
        tooltipRef?.(element);
        (ref as any).current = element;
        cardRef.current = element;
      },
      [ref, tooltipRef],
    );

    const hasTagLabel = !isEmpty(tagGameLabels);
    const hasIconLabel = !isEmpty(iconGameLabels);
    return (
      <a
        ref={mergedRef}
        className={classNames(
          styles.gameCard,
          {
            [styles.onboarding]: onboarding,
            'onboarding-arrow': onboarding,
            [styles.inCarousel]: carousel,
            'animation-popup': animated,
            [styles.withJackpot]: isJackpot,
          },
          'game-card',
        )}
        data-game-id={masterCasinoGameId}
        data-game-name={game.name}
        /*
          The Crown Jackpot badge's CSS hook — the coins this game contributes to. The badge below is
          rendered whenever the game has any; this attribute and `<body>`'s two decide whether it is
          drawn. See `CrownJackpotBadge.module.scss`.
        */
        data-jackpot-coins={jackpotCoinsAttr(game.jackpots)}
        data-testid={duplicate ? undefined : identifier}
        href={`/games/${masterCasinoGameId}`}
        id={duplicate ? undefined : GAME_CARD_ID_PREFIX + masterCasinoGameId}
        onClick={attemptStartingGame}
      >
        {/*
          The artwork box: everything clipped and rounded lives in here. The "Playing now" strip
          below is a SIBLING of it, which is how it escapes `overflow: hidden`. Same split as
          GameCard.scss — see that file's header.
        */}
        <div className={styles.content}>
          {isJackpot && visible && (
            <div className={styles.jackpotParent}>
              <JackpotBalance
                badgeUrl={game.masterCasinoProvider?.jackpotBadgeUrl}
                providerId={game.masterCasinoProviderId}
                providerName={game.masterCasinoProvider?.name}
              />
            </div>
          )}

          {/*
          Crown Jackpot badge (KRA-303). Suppressed on a tile already carrying the legacy Crownslots
          feed badge above — they occupy the same corner, and two jackpot marks stacked on one thumbnail
          reads as a rendering fault rather than as two jackpots.

          Whether it is DRAWN is CSS: this renders for any game with jackpots, and `data-jackpot-coins`
          on the anchor plus two `<body>` attributes decide visibility. See CrownJackpotBadge.module.scss.
        */}
          {game.jackpots?.length ? <CrownJackpotBadge /> : null}
          <PreloadBlur
            className={classNames({ [styles.skeleton]: !isImageLoaded })}
            render={(props) => (
              <div className={styles.container}>
                <img
                  {...props}
                  alt={gameName}
                  className={shouldStickVertically ? styles.thumbnailStickVertically : undefined}
                  crossOrigin="anonymous"
                  data-testid={duplicate ? undefined : identifier}
                  loading="lazy"
                  src={getThumbnailUrl(game)}
                  onLoad={(event) => {
                    handleImageLoad(event);
                    props.onLoad?.();
                  }}
                />
                <div className={styles.labelsContainer}>
                  {hasTagLabel && (
                    <div className={classNames(styles.labels, { [styles.multiple]: tagGameLabels.length > 1 })}>
                      {tagGameLabels
                        .filter(({ name }) => name)
                        .map(({ id, name, gcStyle, scStyle }) => {
                          const style = coinType === 'GC' ? { ...gcStyle } : { ...scStyle };

                          return (
                            <span
                              key={id}
                              className={classNames(styles.gameLabel, {
                                [styles.multiple]: tagGameLabels.length > 1,
                              })}
                              style={style}
                            >
                              <span className={styles.gameLabelInternal}>{name.toUpperCase()}</span>
                            </span>
                          );
                        })}
                    </div>
                  )}
                  {hasIconLabel && (
                    <div className={classNames(styles.iconLabels)}>
                      {iconGameLabels.map(({ id, iconSrc }) => (
                        <img
                          key={id}
                          alt="Game Label"
                          className={styles.iconLabel}
                          crossOrigin="anonymous"
                          src={iconSrc}
                        />
                      ))}
                    </div>
                  )}
                </div>
                {game.isTestingOnly && <TestingBadge />}
              </div>
            )}
          />
        </div>
        {/* Sibling of `.content` so it escapes `overflow: hidden`. */}
        <PlayingNowSlot identifier={identifier} masterCasinoGameId={masterCasinoGameId} />
      </a>
    );
  },
);
